import { AdminRole } from "@repo/api-dtos"
import type { AdminCreatorCode } from "@repo/marketplace-admin"
import { createFileRoute } from "@tanstack/react-router"
import * as React from "react"
import { PageHeader, Section, SectionStack } from "@/components/layout"
import { CreateCreatorCodeForm } from "@/features/creator-codes-admin/CreateCreatorCodeForm"
import { CreatorCodesGrid } from "@/features/creator-codes-admin/CreatorCodesGrid"
import { CreatorGiveDialog } from "@/features/creator-codes-admin/CreatorGiveDialog"
import { useAdminRoles } from "@/hooks/use-admin-roles"
import { useCreatorCodes, useRetireCreatorCode } from "@/queries/marketplace"

export const Route = createFileRoute("/creator-codes/")({
	component: CreatorCodesPage,
})

function CreatorCodesPage() {
	const [page, setPage] = React.useState(1)
	const [limit, setLimit] = React.useState(20)
	const [giveTarget, setGiveTarget] = React.useState<AdminCreatorCode | null>(
		null,
	)
	const { codes, isLoading, error, total, totalPages } = useCreatorCodes({
		page,
		limit,
	})
	const retireMutation = useRetireCreatorCode()

	const handlePaginationChange = React.useCallback(
		(nextPage: number, nextLimit: number) => {
			setPage(nextPage)
			setLimit(nextLimit)
		},
		[],
	)

	const handleRetire = React.useCallback(
		(code: AdminCreatorCode) => {
			if (
				!window.confirm(
					`Retire "${code.code}"? It stops working at checkout right away.`,
				)
			) {
				return
			}
			retireMutation.mutate({ id: code.id })
		},
		[retireMutation],
	)

	const retiringId = retireMutation.isPending
		? (retireMutation.variables?.id ?? null)
		: null
	const { hasRole } = useAdminRoles()
	const canOperate = hasRole(AdminRole.MarketplaceCoupons)

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Creator codes"
				count={!isLoading && !error ? total : undefined}
				description="Referral codes issued to content creators, typed at checkout like any referral code, with their own Give when set"
			/>

			<SectionStack>
				{canOperate ? (
					<Section label="Issue a code">
						<CreateCreatorCodeForm />
					</Section>
				) : null}

				<Section label="Active codes">
					{error ? (
						<p className="text-bad">{error.message}</p>
					) : (
						<CreatorCodesGrid
							codes={codes}
							isLoading={isLoading}
							page={page}
							pageSize={limit}
							total={total}
							totalPages={totalPages}
							onPaginationChange={handlePaginationChange}
							onEditGive={canOperate ? setGiveTarget : null}
							onRetire={canOperate ? handleRetire : null}
							retiringId={retiringId}
						/>
					)}
				</Section>
			</SectionStack>

			{giveTarget ? (
				<CreatorGiveDialog
					code={giveTarget}
					onClose={() => setGiveTarget(null)}
				/>
			) : null}
		</div>
	)
}
